import { Check, ChevronDown, ListChecks } from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@reflex/ui/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@reflex/ui/components/ui/tooltip';
import { cn } from '@reflex/ui/lib/utils';
import {
  AGENT_ROW_FIELD_KEYS,
  AGENT_ROW_FIELD_LABELS,
  SESSION_VIEW_OPTIONS,
  useAgentRowFields,
  useSessionView,
} from '@/components/agent/useSessionView';

export interface AgentSidebarViewMenuProps {
  /** Whether the list is currently in multi-select mode. */
  selectMode: boolean;
  /** Enter select mode, or leave it when already in it. */
  onToggleSelectMode: () => void;
  /** Hide the select entry where multi-select isn't available. */
  canSelect?: boolean;
}

/**
 * The sidebar's list controls, gathered behind one chevron so the filter row
 * keeps a single trailing affordance instead of growing a button per setting.
 *
 * It holds three things in the order you reach for them: how the list is
 * arranged (the view), how much of each row is shown, and the one action that
 * changes the list's mode (select). Views are a radio set — only one can be
 * true — and the row fields are checkboxes, so the menu's shape tells you
 * which choices are exclusive before you read a word.
 */
export function AgentSidebarViewMenu({
  selectMode,
  onToggleSelectMode,
  canSelect = true,
}: AgentSidebarViewMenuProps) {
  const [view, setView] = useSessionView();
  const { fields, toggleField } = useAgentRowFields();
  const activeView = SESSION_VIEW_OPTIONS.find((o) => o.value === view) ?? SESSION_VIEW_OPTIONS[0];
  const hiddenFieldCount = AGENT_ROW_FIELD_KEYS.filter((key) => !fields[key]).length;

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="toolbar-icon"
              aria-label="List options"
              data-testid="agent-sidebar-view-menu"
              className={cn(
                'text-muted-foreground',
                // Anything other than the plain default is worth signalling on
                // the closed trigger, so a hidden field or an unusual view is
                // never a mystery you have to open the menu to explain.
                (selectMode || hiddenFieldCount > 0) && 'text-foreground',
              )}
            >
              <ChevronDown />
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom">{activeView.label}</TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>View</DropdownMenuLabel>
        {/* Deliberately DropdownMenuItem + an explicit check rather than
            RadioItem: the radio dot competes with each view's own icon, and
            the icon is what makes the three scannable. */}
        {SESSION_VIEW_OPTIONS.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onSelect={() => setView(option.value)}
            data-testid={`agent-sidebar-view-${option.value}`}
            aria-checked={option.value === view}
            role="menuitemradio"
          >
            <option.icon className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            <span className="flex-1">{option.label}</span>
            {option.value === view ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : null}
          </DropdownMenuItem>
        ))}

        <DropdownMenuSeparator />
        <DropdownMenuLabel>Show on each row</DropdownMenuLabel>
        {AGENT_ROW_FIELD_KEYS.map((key) => (
          <DropdownMenuCheckboxItem
            key={key}
            checked={fields[key]}
            // Keep the menu open: toggling row fields is a comparison you make
            // against the list behind the menu, usually more than one at a time.
            onSelect={(event) => event.preventDefault()}
            onCheckedChange={() => toggleField(key)}
            data-testid={`agent-sidebar-field-${key}`}
          >
            {AGENT_ROW_FIELD_LABELS[key]}
          </DropdownMenuCheckboxItem>
        ))}

        {canSelect ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={onToggleSelectMode}
              data-testid="agent-sidebar-select-toggle"
            >
              <ListChecks className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
              {selectMode ? 'Done selecting' : 'Select items'}
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
